import { css } from 'styled-components';
import SVGInline from 'components/SVGInline/SVGInline';
import { styled } from 'components/ThemeConfigConnector/ThemeConfigConnnector';
import { Breakpoints, MOBILE_MQ, MQ } from 'style/styled-components/cssMediaQueries';
import { PictureWrapper } from 'components/Picture/Picture.css';
import { fontBold, fontExtraBold, fontNormal } from 'style/styled-components/cssGlobalVariables';
import { LiveProgressIndicatorWrapper } from 'components/LiveProgressIndicator/LiveProgressIndicator';
import { ProgressContainer } from 'components/ProgressIndicator/ProgressIndicator';
import { hexToRgba } from 'utils/fnColors';
import { buttonReset } from 'style/styled-components/cssButtonReset';
import { ResumeProgressIndicatorWrapper } from 'components/Pages/PageDetailCommon/PageDetailCommon.css';
import { ButtonInner, ButtonWrapper } from 'components/Buttons/Button/Button.css';
import { ContentMarker, StyledSVG } from 'style/styled-components/reusable.css';

export const ImageItem = styled.div<{ isSmall: boolean; isOrientationLandscape: boolean }>`
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;

    ${MQ(Breakpoints.s)} {
        width: 90%;
    }

    ${(props) =>
        props.isSmall
            ? css`
                  ${MQ(Breakpoints.s)} {
                      right: 32px;
                      left: unset;
                      width: ${props.isOrientationLandscape ? '288px' : '136px'};
                      height: auto;
                      margin-top: 7.85%;
                  }
                  ${MQ(Breakpoints.m)} {
                      right: 40px;
                      left: unset;
                      width: ${props.isOrientationLandscape ? '384px' : '172px'};
                      margin-top: 7.11%;
                  }
              `
            : ''},

    ${PictureWrapper} {
        img {
            object-position: top center;
        }
    }
`;

export const DarkGradientLeft = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 10%;
    height: 100%;
    background-color: ${(props) => props.theme.brandedConfig.background};
    display: none;

    ${MQ(Breakpoints.xs)} {
        display: block;
    }
`;

export const DarkGradientMiddle = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    visibility: hidden;
    width: 100%;
    height: 100%;

    ${MQ(Breakpoints.xs)} {
        visibility: visible;
        width: 360px;
        left: 10%;
        background: linear-gradient(90deg, ${(props) => props.theme.brandedConfig.background} 0%, transparent 100%);
    }

    ${MQ(Breakpoints.m)} {
        width: 480px;
    }
`;

export const DarkGradientBottom = styled.div`
    position: absolute;
    left: 0;
    bottom: 0;
    background: linear-gradient(
        0deg,
        ${(props) => props.theme.brandedConfig.background} 0%,
        ${(props) => hexToRgba(props.theme.brandedConfig.background, 0)} 100%
    );
    height: 64px;
    width: 100%;

    ${MQ(Breakpoints.xs)} {
        height: 160px;
    }

    ${MQ(Breakpoints.m)} {
        height: 240px;
    }
`;

export const DetailButtons = styled.div`
    margin-top: 10px;
    display: none;

    ${MQ(Breakpoints.xs)} {
        display: flex;
        gap: 10px;
        height: 40px;
    }

    &:after {
        content: '';
        display: block;
        clear: both;
    }

    ${ButtonInner} {
        .SVGInline {
            svg {
                height: 24px;
                width: 24px;
            }
        }

        .SVGInline + span {
            display: none;
        }

        .SVGInline.show-label + span {
            display: initial;
        }
    }
`;

export const DetailButtonsMobile = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-top: 10px;
    align-items: center;
    justify-content: center;

    ${MQ(Breakpoints.xs)} {
        display: none;
    }

    ${ButtonWrapper} {
        &.full-width-on-mobile {
            display: block;
            width: 100%;
            margin-top: 6px;
            margin-bottom: 6px;
        }
    }

    ${ButtonWrapper} {
        &:not(.full-width-on-mobile) {
            margin: 0;
            ${ButtonInner} {
                border: none;
                vertical-align: middle;
                font-family: ${fontNormal};
                font-size: 12px;
                background: transparent;

                &:not(.no-hover):hover {
                    border: none;
                }

                .SVGInline {
                    display: block;
                    margin-bottom: 10px;
                }
            }
        }
    }
`;

export const DetailsDescription = styled.div`
    white-space: normal;
    font-size: 16px;
    line-height: 24px;
    color: ${(props) => props.theme.brandedConfig.high_contrast};
    margin-top: 8px;
    margin-bottom: 8px;

    ${MQ(Breakpoints.s)} {
        text-align: unset;
    }
`;

export const ShowMoreButton = styled.button`
    ${buttonReset}
    cursor: pointer;
    margin: 12px;
    display: none;
    position: relative;

    ${MQ(Breakpoints.s)} {
        display: inline-block;
        margin: 0 0 8px 0;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: ${(props) => props.theme.brandedConfig.high_contrast};
        transition: all 0.2s ease-in-out;

        svg {
            width: 8px;
            height: auto;
            display: inline-block;
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);

            path {
                transition: all 0.2s ease-in-out;
                fill: ${(props) => props.theme.brandedConfig.black_color};
                background-color: ${(props) => props.theme.brandedConfig.white_color};
            }
        }
    }

    &:focus-visible {
        outline: var(--focus-outline);
        transition: none;
    }
`;

export const DetailsDescriptionWrapper = styled.div<{ showMore: boolean }>`
    cursor: ${(props) => (props.showMore ? 'pointer' : 'default')};
    position: relative;
    display: inline-flex;

    align-items: flex-end;
    flex-direction: column;
    margin: 10px 0;

    @media (max-width: ${Breakpoints.xs}) {
        ${DetailsDescription} {
            align-self: stretch;
        }
    }

    ${MQ(Breakpoints.s)} {
        margin: 0 0 0 -8px;
        padding-right: 8px;
        border: 1px solid transparent;
        width: min-content;
        flex-direction: row;
        gap: 0px;
        ${(props) =>
            props.showMore
                ? css`
                      &:hover {
                          background-color: ${hexToRgba(props.theme.brandedConfig.white_color, 0.2)};
                          border-radius: 5px;
                          border: 1px solid ${hexToRgba(props.theme.brandedConfig.white_color, 0.3)};
                      }
                  `
                : css``}

        ${DetailsDescription} {
            margin-left: 8px;
            max-width: 400px;
            width: max-content;
            margin-right: 0;
        }
    }
`;

export const MetaDataContainer = styled.div`
    display: flex;
    gap: 12px;
    line-height: 20px;
    font-size: 14px;
    justify-content: center;
    align-items: center;

    ${MQ(Breakpoints.xs)} {
        justify-content: flex-start;
        align-items: center;
    }

    @media ${MOBILE_MQ} {
        flex-wrap: wrap;
        row-gap: 4px;

        > * {
            white-space: nowrap;
        }
    }

    ${StyledSVG} {
        margin-top: 0;
        vertical-align: middle;
    }
`;

export const IconsContainer = styled.div<{ iconListLength: number; isMobile: boolean }>`
    display: ${(props) => (props.iconListLength > 1 && props.isMobile ? 'block' : 'flex')};
    margin-top: ${(props) => (props.iconListLength > 1 && props.isMobile ? '16px' : '8px')};
    margin-bottom: 8px;
    font-size: 14px;

    @media ${MOBILE_MQ} {
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        row-gap: 8px;
    }

    ${StyledSVG} {
        margin-top: 0;
        vertical-align: middle;
    }
`;

export const DetailsWrapper = styled.div<{ metaData?: any[]; description?: string }>`
    position: relative;
    width: 100%;
    padding: 0 16px;
    text-align: center;

    display: flex;
    flex-direction: column;

    ${ContentMarker} {
        position: relative;
        display: inline-block;
        margin-right: 12px;
        left: 0;
        bottom: 0;
    }

    ${LiveProgressIndicatorWrapper}, ${ResumeProgressIndicatorWrapper} {
        position: relative;
        top: unset;
        margin-top: 8px;
        margin-bottom: 8px;

        ${ProgressContainer} {
            position: relative;

            &:before {
                border-radius: 2px;
            }
        }
    }

    ${MQ(Breakpoints.xs)} {
        text-align: unset;
        padding-left: 32px;
        padding-bottom: 40px;
        padding-top: 140px;
        position: relative;

        ${LiveProgressIndicatorWrapper}, ${ResumeProgressIndicatorWrapper} {
            width: 320px;
        }
    }

    ${MQ(Breakpoints.m)} {
        padding-left: 72px;
        padding-top: 136px;
    }
`;

export const Title = styled.h1`
    font-style: normal;
    font-size: 32px;
    line-height: 40px;
    color: ${(props) => props.theme.brandedConfig.high_contrast};
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: normal;
    font-family: ${fontExtraBold};

    ${MQ(Breakpoints.xs)} {
        max-width: 480px;
        margin-top: 0;
        text-align: unset;
        -webkit-line-clamp: 2;
    }
`;

export const SubTitle = styled.h2`
    font-style: normal;
    font-weight: normal;
    font-size: 20px;
    line-height: 24px;
    color: ${(props) => props.theme.brandedConfig.high_contrast};
    margin-bottom: 8px;
    margin-top: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    ${MQ(Breakpoints.xs)} {
        max-width: 480px;
        text-align: unset;
    }
`;

export const ImageWrapper = styled.div`
    position: relative;
    height: 56.11vw;
    width: 100%;

    ${MQ(Breakpoints.xs)} {
        position: absolute;
    }

    ${MQ(Breakpoints.m)} {
        height: 35vw;
        position: absolute;
        max-height: 840px;
    }
`;

export const StyledSVGInline = styled(SVGInline)`
    height: 24px;
    width: auto;
    display: inline-block;
    position: relative;
`;

export const ShowMoreTextWrapper = styled.div`
    cursor: pointer;
    right: 0;
    display: block;
    color: ${(props) => props.theme.brandedConfig.primary};
    margin: 0 8px;

    font-size: 16px;
    line-height: 24px;
    bottom: 8px;

    ${MQ(Breakpoints.s)} {
        display: none;
    }
`;

export const ShowMoreText = styled.span`
    cursor: pointer;
    color: ${(props) => props.theme.brandedConfig.white_color};
    font-family: ${fontBold};
    text-transform: uppercase;
`;

export const ShowMoreTextDots = styled.span`
    color: ${(props) => props.theme.brandedConfig.high_contrast};
    padding-right: 8px;
`;

export const PageHeader = styled.div`
    max-width: 100%;
    position: relative;
    padding: 0;
    margin-bottom: 0;
    width: 100%;
`;

export const ContentMarkerMobile = styled.div`
    position: absolute;
    left: 0;
    bottom: 8px;
    z-index: 2;

    ${MQ(Breakpoints.s)} {
        display: none;
    }

    ${ContentMarker} {
        position: static;
    }
`;

export const ContentMarkerDesktop = styled.div`
    position: absolute;
    left: 0;
    bottom: 8px;
    display: none;

    ${MQ(Breakpoints.s)} {
        position: static;
        margin: 8px 0;
        float: left;
        display: block;
    }
`;

export const InfoLabel = styled.div`
    display: flex;
    align-items: center;
    text-align: left;
    line-height: 1.2;
    font-size: 14px;
    gap: 8px;
    width: 420px;
    padding: 8px 16px 8px 8px;
    margin: 8px 0 14px;
    border-radius: 4px;
    background: var(--color-dark-foreground);
    color: var(--color-primary);

    .SVGInline {
        display: flex;
    }
`;

export const InfoLabelMobile = styled(InfoLabel)`
    width: 100%;

    ${MQ(Breakpoints.xs)} {
        display: none;
    }
`;

export const InfoLabelDesktop = styled(InfoLabel)`
    display: none;

    ${MQ(Breakpoints.xs)} {
        display: flex;
    }
`;
